import { FC } from 'react';
import { DagStatus } from '@agilelab/plugin-wb-builder-common';
import {
  Status as PolicyStatus,
  Severity,
} from '@agilelab/plugin-wb-governance-common';
import {
  createStyles,
  makeStyles,
  Theme,
  Tooltip,
  useTheme,
} from '@material-ui/core';
import AutorenewIcon from '@material-ui/icons/Autorenew';
import ErrorIcon from '@material-ui/icons/Error';
import { TerminatingIcon } from '../../ReleaseDetailPage/ProvisioningCard/LatestProvisioningAccordion/Icons';
import {
  KoFilledIcon,
  KoIcon,
  NotStartedIcon,
  OkFilledIcon,
  OkIcon,
  OkPlusIcon,
  TerminatedIcon,
} from '../Icons';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    detailIcon: {
      height: '24px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginRight: theme.spacing(2),
    },
    icon: {
      width: '1.3em',
      height: '1.3em',
    },
    animatedIcon: {
      animation: '$spin 2s linear infinite',
    },
    '@keyframes spin': {
      '0%': {
        transform: 'rotate(360deg)',
      },
      '100%': {
        transform: 'rotate(0deg)',
      },
    },
  }),
);

export const DetailIcon: FC<{
  status: DagStatus;
  okWithWarnings?: boolean;
  filled?: boolean;
  severity?: Severity;
  policyStatus?: PolicyStatus;
}> = ({ status, filled, severity, policyStatus, okWithWarnings }) => {
  const classes = useStyles();
  const theme = useTheme();

  return (
    <div className={classes.detailIcon}>
      {
        {
          [DagStatus.FAILED]: (
            <Tooltip title="Failed. You need to solve the errors reported since they will prevent the deployment">
              {filled ? (
                <KoFilledIcon
                  htmlColor={theme.palette.error.main}
                  className={classes.icon}
                />
              ) : (
                <KoIcon
                  htmlColor={theme.palette.error.main}
                  className={classes.icon}
                />
              )}
            </Tooltip>
          ),
          [DagStatus.ERROR]: (
            <Tooltip title="Failed. You need to solve the errors reported since they will prevent the deployment">
              <KoFilledIcon
                htmlColor={theme.palette.error.main}
                className={classes.icon}
              />
            </Tooltip>
          ),
          [DagStatus.PASSED]: (
            <Tooltip
              title={
                okWithWarnings
                  ? 'Completed with warnings'
                  : 'Completed with success'
              }
            >
              {/* eslint-disable no-nested-ternary */}
              {filled ? (
                <OkFilledIcon
                  htmlColor={theme.palette.success.main}
                  className={classes.icon}
                />
              ) : okWithWarnings ? (
                <OkPlusIcon
                  htmlColor={theme.palette.success.main}
                  className={classes.icon}
                />
              ) : (
                <OkIcon
                  htmlColor={theme.palette.success.main}
                  className={classes.icon}
                />
              )}
            </Tooltip>
          ),
          [DagStatus.NOT_EXECUTED]: (
            <Tooltip title="Failed. You need to solve the errors reported since they will prevent the deployment">
              <KoFilledIcon
                htmlColor={theme.palette.error.main}
                className={classes.icon}
              />
            </Tooltip>
          ),
          [DagStatus.NOT_STARTED]: (
            <Tooltip title="At least one error was encountered in this execution due to a previous policy, so this check was not executed at all">
              <NotStartedIcon className={`${classes.icon}`} />
            </Tooltip>
          ),
          [DagStatus.TERMINATED]: (
            <Tooltip title="The execution has been terminated">
              <TerminatedIcon className={`${classes.icon}`} />
            </Tooltip>
          ),
          [DagStatus.TERMINATING]: (
            <Tooltip title="The execution is being terminated">
              <TerminatingIcon className={`${classes.icon}`} />
            </Tooltip>
          ),
          [DagStatus.RUNNING]: (
            <AutorenewIcon
              className={`${classes.icon} ${classes.animatedIcon}`}
            />
          ),
          [DagStatus.WAITING]: (
            <AutorenewIcon
              className={`${classes.icon} ${classes.animatedIcon}`}
            />
          ),
          [DagStatus.INFO]: (
            <Tooltip
              title={`Failed with severity ${severity}. This policy is just a warning, and will not block the deployment, but remember to check what errors made it fail`}
            >
              <ErrorIcon
                className={`${classes.icon}`}
                htmlColor={theme.palette.primary.main}
              />
            </Tooltip>
          ),
          [DagStatus.NOT_BLOCKING_ERROR]: (
            <Tooltip
              title={
                policyStatus === PolicyStatus.Grace
                  ? 'Failed. This policy is in the grace period, so it is not blocking the deployment. Check and solve the errors, since they will block the deployment when this will become enabled'
                  : 'Failed. This policy is deprecated, so it is not blocking the deployment. Check the errors, but remember that this policy will be removed'
              }
            >
              <ErrorIcon
                className={`${classes.icon}`}
                htmlColor={theme.palette.warning.main}
              />
            </Tooltip>
          ),
          [DagStatus.OK]: (
            <Tooltip title="Compliant">
              <OkFilledIcon
                htmlColor={theme.palette.success.main}
                className={classes.icon}
              />
            </Tooltip>
          ),
          [DagStatus.WARNING]: (
            <Tooltip
              title={`Failed with severity ${severity}. This policy is just a warning, and will not block the deployment, but remember to check what errors made it fail`}
            >
              <ErrorIcon
                className={`${classes.icon}`}
                htmlColor={theme.palette.warning.main}
              />
            </Tooltip>
          ),
        }[status]
      }
    </div>
  );
};
